import { SOURCE_CONTEXT_NAMES, WIDGET_LOGICAL_NAMES } from '@config/Consts';
import BaseService from '@core/common/BaseService';
import BaseClient from '@core/common/BaseClient';
import { ConversationBuilderClient, RESOURCES } from '@core/clients/ConversationBuilderClient';

function getClient(params) {
  return new ConversationBuilderClient(params);
}

function getLoginClient() {
  return getClient({
    resource: RESOURCES.BOT_SSO,
    contextName: SOURCE_CONTEXT_NAMES[WIDGET_LOGICAL_NAMES.CONVERSATIONS],
  });
}

function getDomainsClient(credentials) {
  return getClient({
    infraBaseClientOptions: {
      config: {
        isExternalApi: true,
        headers: {
          Authorization: credentials.apiAccessToken,
          OrganizationId: credentials.sessionOrganizationId,
        },
      },
    },
    resource: RESOURCES.BOT_PLATFORM_DOMAINS,
    contextName: SOURCE_CONTEXT_NAMES[WIDGET_LOGICAL_NAMES.CONVERSATIONS],
  });
}

function getIntentsClient(credentials) {
  return getClient({
    infraBaseClientOptions: {
      config: {
        isExternalApi: true,
        headers: {
          Authorization: credentials.apiAccessToken,
          OrganizationId: credentials.sessionOrganizationId,
        },
      },
    },
    resource: RESOURCES.BOT_PLATFORM_INTENTS,
    contextName: SOURCE_CONTEXT_NAMES[WIDGET_LOGICAL_NAMES.CONVERSATIONS],
  });
}

async function login() {
  const loginClient = getLoginClient();
  return loginClient.sendRequest({
    method: BaseClient.HTTP_METHODS.GET,
  })
    .then((result) => {
      const data = result.data.successResult;
      return {
        apiAccessToken: data.apiAccessToken,
        sessionOrganizationId: data.sessionOrganizationId,
      };
    });
}

async function fetchDomains(credentials) {
  const domainsClient = getDomainsClient(credentials);
  return domainsClient.sendRequest({
    method: BaseClient.HTTP_METHODS.GET, body: {},
  })
    .then((result) => {
      const domainsList = result.data.successResult.DomainList;
      const liveIntentDomains = domainsList.filter(domain => domain.hasLiveIntents);
      const domainIds = liveIntentDomains.map(({ id }) => ({ id }));
      return domainIds;
    });
}

async function fetchIntentsByDomains(credentials, domains) {
  const intentsPerDomain = [];
  const intentsClient = getIntentsClient(credentials);
  await Promise.all(domains.map(domain => intentsClient.sendRequest({
    method: BaseClient.HTTP_METHODS.GET, url: domain.id, body: {},
  })
    .then((result) => {
      const intentsData = result.data.successResult.intents;
      intentsPerDomain.push({
        domain: domain.id,
        intents: intentsData.filter(intent => intent.isLiveIntent)
          .map(({ id, name }) => ({ id, name })),
      });
    })));
  return intentsPerDomain;
}

/**
 ** This service is used to access the ConversationBuilder api to get domains and intents
 * that were configured in Intents Builder */

class ConversationBuilderService extends BaseService {
  constructor() {
    super({ name: 'ConversationBuilderService' });
  }

  /**
   1. Login to Conversation Builder to get organisation ID and token
   2. Retrieve the domains by organisation ID
   3. Retrieve the intents by domain
   */
  getLiveIntents = async () => {
    const credentials = await login();
    const domains = await fetchDomains(credentials);
    const intents = await fetchIntentsByDomains(credentials, domains);
    return intents;
  };
}

const conversationBuilderService = new ConversationBuilderService();

export default conversationBuilderService;

